import React, { useState, useEffect, useRef } from "react";
import { useAction } from "wasp/client/operations";
import { TasteProfileStep } from "./TasteProfileStep";
import { TasteProfileProgress } from "./TasteProfileProgress";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Checkbox } from "../../../components/ui/checkbox";
import { Slider } from "../../../components/ui/slider";
import { Switch } from "../../../components/ui/switch";
import { ChoiceCard } from "../../../components/ChoiceCard";
import { motion } from "framer-motion";
import {
  Coffee,
  Sun,
  Moon,
  Apple,
  DollarSign,
  TrendingUp,
  Star,
  Smile,
  ChefHat,
  Award,
} from "lucide-react";
import type { TasteProfileData } from "./TasteProfileOnboardingFlow.container";
import { ColdStartRecipeSelector } from "../ColdStartRecipeSelector";
import { PostOnboardingRecipeCarousel } from "./PostOnboardingRecipeCarousel";

// Pure UI component props interface
interface TasteProfileOnboardingFlowViewProps {
  // Data
  formData: TasteProfileData;
  currentStep: number;
  totalSteps: number;
  isSubmitting: boolean;

  // Computed values
  isStepValid: boolean;

  // Configuration options
  dietTypes: Array<{
    id: string;
    emoji: string;
    title: string;
    description: string;
  }>;
  commonAllergies: string[];
  commonRestrictions: string[];
  goals: Array<{
    id: string;
    emoji: string;
    title: string;
    description: string;
  }>;
  equipment: Array<{
    id: string;
    emoji: string;
    title: string;
  }>;
  batchPreferences: Array<{
    id: string;
    emoji: string;
    title: string;
    description: string;
  }>;
  spiceLevels: Array<{
    id: string;
    emoji: string;
    title: string;
    description: string;
  }>;
  cookingTimes: Array<{
    id: string;
    title: string;
    description: string;
  }>;
  cuisines: string[];
  pantryStaples: string[];
  texturePreferences: string[];
  cookingMethods: string[];
  emailPreferenceOptions: Array<{
    key: string;
    label: string;
    description: string;
  }>;
  notificationOptions: Array<{
    key: string;
    label: string;
    description: string;
  }>;

  // NEW: Enhanced configuration options from NuraOnboarding
  countries: Array<{
    code: string;
    name: string;
    currency: string;
    flag: string;
  }>;
  reasonOptions: string[];
  householdOptions: Array<{
    id: number;
    label: string;
  }>;

  // NEW: Meal Planning Configuration Options
  mealTypeOptions: Array<{
    id: string;
    icon: string;
    title: string;
    description: string;
  }>;
  budgetOptions: Array<{
    id: string;
    icon: string;
    title: string;
    description: string;
  }>;
  difficultyOptions: Array<{
    id: string;
    icon: string;
    title: string;
    description: string;
  }>;

  // Event handlers
  updateFormData: (data: Partial<TasteProfileData>) => void;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  onComplete: (recipeIds?: string[]) => void;

  // Recipe selection (Step 6)
  selectedRecipeIds?: string[];
  onRecipeSelect?: (recipeId: string) => void;
}

/**
 * TasteProfileOnboardingFlow View - Pure UI component with no hooks or business logic
 *
 * This component only handles:
 * - Rendering the 8-step onboarding flow UI
 * - Step-specific content rendering
 * - Form input interactions (delegated to container via props)
 * - UI animations and transitions
 * - No hooks, no state, no side effects
 */
export default function TasteProfileOnboardingFlowView({
  // Data
  formData,
  currentStep,
  totalSteps,
  isSubmitting,

  // Computed values
  isStepValid,

  // Configuration options
  dietTypes,
  commonAllergies,
  commonRestrictions,
  goals,
  equipment,
  batchPreferences,
  spiceLevels,
  cookingTimes,
  cuisines,
  pantryStaples,
  texturePreferences,
  cookingMethods,
  emailPreferenceOptions,
  notificationOptions,

  // NEW: Enhanced configuration options
  countries,
  reasonOptions,
  householdOptions,

  // NEW: Meal Planning Configuration Options
  mealTypeOptions,
  budgetOptions,
  difficultyOptions,

  // Event handlers
  updateFormData,
  onNext,
  onBack,
  onSkip,
  onComplete,

  // Recipe selection (Step 6)
  selectedRecipeIds = [],
  onRecipeSelect,
}: TasteProfileOnboardingFlowViewProps) {
  // Pure UI function: Step content rendering logic
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <EssentialsStep
            formData={formData}
            updateFormData={updateFormData}
            countries={countries}
            reasonOptions={reasonOptions}
            householdOptions={householdOptions}
          />
        );
      case 2:
        return (
          <DietaryAndGoalsStep
            formData={formData}
            updateFormData={updateFormData}
            dietTypes={dietTypes}
            commonAllergies={commonAllergies}
            commonRestrictions={commonRestrictions}
            goals={goals}
          />
        );
      case 3:
        return (
          <KitchenAndEquipmentStep
            formData={formData}
            updateFormData={updateFormData}
            equipment={equipment}
            batchPreferences={batchPreferences}
          />
        );
      case 4:
        return (
          <TastePreferencesStep
            formData={formData}
            updateFormData={updateFormData}
            spiceLevels={spiceLevels}
            cookingTimes={cookingTimes}
            cuisines={cuisines}
            pantryStaples={pantryStaples}
            texturePreferences={texturePreferences}
            cookingMethods={cookingMethods}
          />
        );
      case 5:
        return (
          <ReviewStep formData={formData} updateFormData={updateFormData} />
        );
      case 6:
        return (
          <PostOnboardingRecipeCarousel
            formData={formData}
            selectedRecipeIds={selectedRecipeIds}
            onRecipeSelect={onRecipeSelect}
            onSwipeClick={(recipeIds) => onComplete(selectedRecipeIds)}
            onDiscoverClick={(recipeIds) => onComplete(selectedRecipeIds)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-4xl p-6">
        <TasteProfileProgress
          currentStep={currentStep}
          totalSteps={totalSteps}
          completedSteps={formData.completedSteps ?? []}
        />

        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          {renderStepContent()}
        </motion.div>

        <div className="mt-8 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={onBack}
            disabled={currentStep === 1}
          >
            Back
          </Button>

          <div className="flex gap-3">
            {currentStep < totalSteps && currentStep > 2 && (
              <Button variant="ghost" onClick={onSkip}>
                Skip for now
              </Button>
            )}

            {currentStep === totalSteps ? (
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  onComplete();
                }}
                disabled={isSubmitting}
                className="min-w-32 bg-coral text-white hover:bg-coral/90"
              >
                {isSubmitting ? "Saving..." : "Complete Profile"}
              </Button>
            ) : (
              <Button
                onClick={onNext}
                disabled={!isStepValid}
                className="min-w-32 bg-coral text-white hover:bg-coral/90"
              >
                Continue
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// Individual step components - Pure UI functions

// NEW: Region & Personalization Step (from NuraOnboarding integration)
function RegionPersonalizationStep({
  formData,
  updateFormData,
  countries,
  reasonOptions,
  householdOptions,
}: {
  formData: Partial<TasteProfileData>;
  updateFormData: (data: Partial<TasteProfileData>) => void;
  countries: Array<{
    code: string;
    name: string;
    currency: string;
    flag: string;
  }>;
  reasonOptions: string[];
  householdOptions: Array<{
    id: number;
    label: string;
  }>;
}) {
  const toggleReason = (reason: string) => {
    const current = formData.reasons || [];
    const updated = current.includes(reason)
      ? current.filter((r) => r !== reason)
      : [...current, reason];
    updateFormData({ reasons: updated });
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-2 text-2xl font-medium">
          Welcome! Let's Get Started
        </h2>
        <p className="text-muted-foreground">
          Tell us about yourself to create the perfect meal planning experience
        </p>
      </div>

      <div className="space-y-6">
        {/* Household Size */}
        <div>
          <Label className="mb-4 block text-base font-medium">
            How many people are you cooking for?
          </Label>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {householdOptions.map((option) => (
              <Button
                key={option.id}
                type="button"
                variant={
                  formData.householdSize === option.id ? "default" : "outline"
                }
                className={
                  formData.householdSize === option.id
                    ? "border-coral bg-coral text-white"
                    : "hover:bg-stone/20 border-border"
                }
                onClick={() => updateFormData({ householdSize: option.id })}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Region/Country Selection with Comprehensive List */}
        <div>
          <Label className="mb-4 block text-base font-medium">
            Where are you shopping from?
            <span className="mt-1 block text-sm font-normal text-muted-foreground">
              Used to estimate grocery costs and show local currency
            </span>
          </Label>
          <Select
            value={formData.regionCountry || "US"}
            onValueChange={(value) => {
              const selectedCountry = countries.find((c) => c.code === value);
              updateFormData({
                regionCountry: value,
                regionCurrency: selectedCountry?.currency || "USD",
              });
            }}
          >
            <SelectTrigger>
              <SelectValue>
                {formData.regionCountry &&
                countries.find((c) => c.code === formData.regionCountry) ? (
                  <span className="flex items-center gap-2">
                    <span>
                      {
                        countries.find((c) => c.code === formData.regionCountry)
                          ?.flag
                      }
                    </span>
                    <span>
                      {
                        countries.find((c) => c.code === formData.regionCountry)
                          ?.name
                      }
                    </span>
                    <span className="text-muted-foreground">
                      (
                      {
                        countries.find((c) => c.code === formData.regionCountry)
                          ?.currency
                      }
                      )
                    </span>
                  </span>
                ) : (
                  "Select your country"
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {countries.map((country) => (
                <SelectItem key={country.code} value={country.code}>
                  <span className="flex items-center gap-2">
                    <span>{country.flag}</span>
                    <span>{country.name}</span>
                    <span className="text-muted-foreground">
                      ({country.currency})
                    </span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Why are you here? (Multi-selection from NuraOnboarding) */}
        <div>
          <Label className="mb-4 block text-base font-medium">
            What brings you to meal planning today?
            <span className="mt-1 block text-sm font-normal text-muted-foreground">
              Pick one or more — you can change this anytime
            </span>
          </Label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {reasonOptions.map((reason) => (
              <button
                key={reason}
                type="button"
                onClick={() => toggleReason(reason)}
                className={`rounded-lg border p-3 text-left text-sm transition-all ${
                  formData.reasons?.includes(reason)
                    ? "border-coral bg-coral/10 text-ink"
                    : "hover:bg-stone/20 border-border bg-white text-ink"
                }`}
              >
                {reason}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function DietaryStep({
  formData,
  updateFormData,
  dietTypes,
  commonAllergies,
  commonRestrictions,
}: {
  formData: Partial<TasteProfileData>;
  updateFormData: (data: Partial<TasteProfileData>) => void;
  dietTypes: Array<{
    id: string;
    emoji: string;
    title: string;
    description: string;
  }>;
  commonAllergies: string[];
  commonRestrictions: string[];
}) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-2 text-2xl font-medium">
          Dietary Preferences & Restrictions
        </h2>
        <p className="text-muted-foreground">
          Help us understand your dietary needs and preferences
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <Label className="mb-4 block text-base font-medium">
            What's your primary diet type?
          </Label>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {dietTypes.map((diet) => (
              <ChoiceCard
                key={diet.id}
                emoji={diet.emoji}
                title={diet.title}
                description={diet.description}
                selected={formData.dietType === diet.id}
                onClick={() => updateFormData({ dietType: diet.id })}
              />
            ))}
          </div>
        </div>

        <div>
          <Label className="mb-4 block text-base font-medium">
            Any allergies? (Select all that apply)
          </Label>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {commonAllergies.map((allergy) => (
              <div key={allergy} className="flex items-center space-x-2">
                <Checkbox
                  id={`allergy-${allergy}`}
                  checked={formData.allergies?.includes(allergy) || false}
                  onCheckedChange={(checked) => {
                    const current = formData.allergies || [];
                    const updated = checked
                      ? [...current, allergy]
                      : current.filter((a) => a !== allergy);
                    updateFormData({ allergies: updated });
                  }}
                />
                <Label
                  htmlFor={`allergy-${allergy}`}
                  className="text-sm capitalize"
                >
                  {allergy}
                </Label>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Label className="mb-4 block text-base font-medium">
            Any dietary restrictions? (Select all that apply)
          </Label>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {commonRestrictions.map((restriction) => (
              <div key={restriction} className="flex items-center space-x-2">
                <Checkbox
                  id={`restriction-${restriction}`}
                  checked={
                    formData.dietaryRestrictions?.includes(restriction) || false
                  }
                  onCheckedChange={(checked) => {
                    const current = formData.dietaryRestrictions || [];
                    const updated = checked
                      ? [...current, restriction]
                      : current.filter((r) => r !== restriction);
                    updateFormData({ dietaryRestrictions: updated });
                  }}
                />
                <Label
                  htmlFor={`restriction-${restriction}`}
                  className="text-sm capitalize"
                >
                  {restriction}
                </Label>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <Label htmlFor="likes" className="mb-2 block text-base font-medium">
              Foods you love
            </Label>
            <Textarea
              id="likes"
              placeholder="e.g., spicy food, chocolate, fresh herbs..."
              value={formData.likes?.join(", ") || ""}
              onChange={(e) =>
                updateFormData({
                  likes: e.target.value
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
              rows={3}
            />
          </div>

          <div>
            <Label
              htmlFor="dislikes"
              className="mb-2 block text-base font-medium"
            >
              Foods you avoid
            </Label>
            <Textarea
              id="dislikes"
              placeholder="e.g., mushrooms, cilantro, overly sweet dishes..."
              value={formData.dislikes?.join(", ") || ""}
              onChange={(e) =>
                updateFormData({
                  dislikes: e.target.value
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
              rows={3}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function GoalsStep({
  formData,
  updateFormData,
  goals,
}: {
  formData: Partial<TasteProfileData>;
  updateFormData: (data: Partial<TasteProfileData>) => void;
  goals: Array<{
    id: string;
    emoji: string;
    title: string;
    description: string;
  }>;
}) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-2 text-2xl font-medium">Goals & Nutrition Targets</h2>
        <p className="text-muted-foreground">
          What are you hoping to achieve with your meal planning?
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <Label className="mb-4 block text-base font-medium">
            What's your primary goal?
          </Label>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {goals.map((goal) => (
              <ChoiceCard
                key={goal.id}
                emoji={goal.emoji}
                title={goal.title}
                description={goal.description}
                selected={formData.primaryGoal === goal.id}
                onClick={() => updateFormData({ primaryGoal: goal.id })}
              />
            ))}
          </div>
        </div>

        {(formData.primaryGoal === "weight-loss" ||
          formData.primaryGoal === "weight-gain" ||
          formData.primaryGoal === "muscle-building") && (
          <Card className="border-coral/20 bg-coral/5">
            <CardContent className="p-6">
              <h3 className="mb-4 font-medium">Macro Targets (Optional)</h3>
              <p className="mb-6 text-sm text-muted-foreground">
                Set your daily macro targets for more precise meal planning
              </p>

              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <div>
                  <Label htmlFor="calories" className="text-sm">
                    Daily Calories
                  </Label>
                  <Input
                    id="calories"
                    type="number"
                    placeholder="2000"
                    value={formData.macroTargets?.calories?.toString() ?? ""}
                    onChange={(e) =>
                      updateFormData({
                        macroTargets: {
                          ...(formData.macroTargets || {
                            calories: 2000,
                            protein: 150,
                            carbs: 200,
                            fat: 65,
                          }),
                          calories: e.target.value
                            ? parseInt(e.target.value)
                            : 2000,
                        },
                      })
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="protein" className="text-sm">
                    Protein (g)
                  </Label>
                  <Input
                    id="protein"
                    type="number"
                    placeholder="150"
                    value={formData.macroTargets?.protein?.toString() ?? ""}
                    onChange={(e) =>
                      updateFormData({
                        macroTargets: {
                          ...(formData.macroTargets || {
                            calories: 2000,
                            protein: 150,
                            carbs: 200,
                            fat: 65,
                          }),
                          protein: e.target.value
                            ? parseInt(e.target.value)
                            : 150,
                        },
                      })
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="carbs" className="text-sm">
                    Carbs (g)
                  </Label>
                  <Input
                    id="carbs"
                    type="number"
                    placeholder="200"
                    value={formData.macroTargets?.carbs?.toString() ?? ""}
                    onChange={(e) =>
                      updateFormData({
                        macroTargets: {
                          ...(formData.macroTargets || {
                            calories: 2000,
                            protein: 150,
                            carbs: 200,
                            fat: 65,
                          }),
                          carbs: e.target.value
                            ? parseInt(e.target.value)
                            : 200,
                        },
                      })
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="fat" className="text-sm">
                    Fat (g)
                  </Label>
                  <Input
                    id="fat"
                    type="number"
                    placeholder="65"
                    value={formData.macroTargets?.fat?.toString() ?? ""}
                    onChange={(e) =>
                      updateFormData({
                        macroTargets: {
                          ...(formData.macroTargets || {
                            calories: 2000,
                            protein: 150,
                            carbs: 200,
                            fat: 65,
                          }),
                          fat: e.target.value ? parseInt(e.target.value) : 65,
                        },
                      })
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function KitchenAndEquipmentStep({
  formData,
  updateFormData,
  equipment,
  batchPreferences,
}: {
  formData: Partial<TasteProfileData>;
  updateFormData: (data: Partial<TasteProfileData>) => void;
  equipment: Array<{
    id: string;
    emoji: string;
    title: string;
  }>;
  batchPreferences: Array<{
    id: string;
    emoji: string;
    title: string;
    description: string;
  }>;
}) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-2 text-2xl font-medium">Kitchen & Equipment</h2>
        <p className="text-muted-foreground">
          Tell us about your cooking setup and meal prep preferences
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <Label className="mb-4 block text-base font-medium">
            What kitchen equipment do you have? (Select all that apply)
          </Label>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {equipment.map((item) => (
              <div
                key={item.id}
                className={`cursor-pointer rounded-lg border p-3 text-center transition-all ${
                  formData.availableEquipment?.includes(item.id)
                    ? "border-coral bg-coral text-white"
                    : "hover:bg-stone/20 border-border bg-white"
                }`}
                onClick={() => {
                  const current = formData.availableEquipment || [];
                  const updated = current.includes(item.id)
                    ? current.filter((e) => e !== item.id)
                    : [...current, item.id];
                  updateFormData({ availableEquipment: updated });
                }}
              >
                <div className="mb-1 text-xl">{item.emoji}</div>
                <div className="text-sm">{item.title}</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Label className="mb-4 block text-base font-medium">
            How do you prefer to prep meals?
          </Label>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {batchPreferences.map((pref) => (
              <ChoiceCard
                key={pref.id}
                emoji={pref.emoji}
                title={pref.title}
                description={pref.description}
                selected={formData.batchPrepPreference === pref.id}
                onClick={() => updateFormData({ batchPrepPreference: pref.id })}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PreferencesStep({
  formData,
  updateFormData,
  spiceLevels,
  cookingTimes,
  cuisines,
  pantryStaples,
}: {
  formData: Partial<TasteProfileData>;
  updateFormData: (data: Partial<TasteProfileData>) => void;
  spiceLevels: Array<{
    id: string;
    emoji: string;
    title: string;
    description: string;
  }>;
  cookingTimes: Array<{
    id: string;
    title: string;
    description: string;
  }>;
  cuisines: string[];
  pantryStaples: string[];
}) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-2 text-2xl font-medium">Preferences & Pantry</h2>
        <p className="text-muted-foreground">
          Help us understand your taste preferences and kitchen staples
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <Label className="mb-4 block text-base font-medium">
            What's your spice tolerance?
          </Label>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {spiceLevels.map((level) => (
              <ChoiceCard
                key={level.id}
                emoji={level.emoji}
                title={level.title}
                description={level.description}
                selected={formData.spiceLevel === level.id}
                onClick={() => updateFormData({ spiceLevel: level.id })}
              />
            ))}
          </div>
        </div>

        <div>
          <Label className="mb-4 block text-base font-medium">
            How much time do you want to spend cooking?
          </Label>
          <Select
            value={formData.cookingTime || ""}
            onValueChange={(value: string) =>
              updateFormData({ cookingTime: value || "" })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select cooking time preference" />
            </SelectTrigger>
            <SelectContent>
              {cookingTimes.map((time) => (
                <SelectItem key={time.id} value={time.id}>
                  {time.title} - {time.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="mb-4 block text-base font-medium">
            Favorite cuisines (Select all that apply)
          </Label>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {cuisines.map((cuisine) => (
              <div key={cuisine} className="flex items-center space-x-2">
                <Checkbox
                  id={`cuisine-${cuisine}`}
                  checked={
                    formData.favoriteCuisines?.includes(cuisine) || false
                  }
                  onCheckedChange={(checked) => {
                    const current = formData.favoriteCuisines || [];
                    const updated = checked
                      ? [...current, cuisine]
                      : current.filter((c) => c !== cuisine);
                    updateFormData({ favoriteCuisines: updated });
                  }}
                />
                <Label htmlFor={`cuisine-${cuisine}`} className="text-sm">
                  {cuisine}
                </Label>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Label className="mb-4 block text-base font-medium">
            Common pantry staples you usually have
          </Label>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {pantryStaples.map((staple) => (
              <div key={staple} className="flex items-center space-x-2">
                <Checkbox
                  id={`staple-${staple}`}
                  checked={formData.pantryStaples?.includes(staple) || false}
                  onCheckedChange={(checked) => {
                    const current = formData.pantryStaples || [];
                    const updated = checked
                      ? [...current, staple]
                      : current.filter((s) => s !== staple);
                    updateFormData({ pantryStaples: updated });
                  }}
                />
                <Label htmlFor={`staple-${staple}`} className="text-sm">
                  {staple}
                </Label>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Label
            htmlFor="favorite-ingredients"
            className="mb-2 block text-base font-medium"
          >
            Favorite ingredients (optional)
          </Label>
          <Textarea
            id="favorite-ingredients"
            placeholder="e.g., fresh basil, avocados, cheese, bacon..."
            value={formData.favoriteIngredients?.join(", ") || ""}
            onChange={(e) =>
              updateFormData({
                favoriteIngredients: e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
            rows={3}
          />
        </div>
      </div>
    </div>
  );
}

function UnitsStep({
  formData,
  updateFormData,
}: {
  formData: Partial<TasteProfileData>;
  updateFormData: (data: Partial<TasteProfileData>) => void;
}) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-2 text-2xl font-medium">Units & Measurements</h2>
        <p className="text-muted-foreground">
          Set your preferred measurement system
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <Label className="mb-4 block text-base font-medium">
            Unit System
          </Label>
          <Select
            value={formData.unitSystem || "imperial"}
            onValueChange={(value: string) =>
              updateFormData({
                unitSystem: (value || "imperial") as "imperial" | "metric",
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="imperial">
                Imperial (cups, pounds, °F)
              </SelectItem>
              <SelectItem value="metric">
                Metric (milliliters, grams, °C)
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div>
            <Label className="mb-4 block text-base font-medium">
              Temperature
            </Label>
            <Select
              value={formData.temperatureUnit || "fahrenheit"}
              onValueChange={(value: string) =>
                updateFormData({
                  temperatureUnit: (value || "fahrenheit") as
                    | "fahrenheit"
                    | "celsius",
                })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fahrenheit">Fahrenheit (°F)</SelectItem>
                <SelectItem value="celsius">Celsius (°C)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="mb-4 block text-base font-medium">Volume</Label>
            <Select
              value={formData.volumeUnit || "cups"}
              onValueChange={(value: string) =>
                updateFormData({ volumeUnit: value || "cups" })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cups">Cups</SelectItem>
                <SelectItem value="milliliters">Milliliters</SelectItem>
                <SelectItem value="liters">Liters</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="mb-4 block text-base font-medium">Weight</Label>
            <Select
              value={formData.weightUnit || "pounds"}
              onValueChange={(value: string) =>
                updateFormData({ weightUnit: value || "pounds" })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pounds">Pounds</SelectItem>
                <SelectItem value="ounces">Ounces</SelectItem>
                <SelectItem value="grams">Grams</SelectItem>
                <SelectItem value="kilograms">Kilograms</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
}

function NotificationsStep({
  formData,
  updateFormData,
  emailPreferenceOptions,
  notificationOptions,
}: {
  formData: Partial<TasteProfileData>;
  updateFormData: (data: Partial<TasteProfileData>) => void;
  emailPreferenceOptions: Array<{
    key: string;
    label: string;
    description: string;
  }>;
  notificationOptions: Array<{
    key: string;
    label: string;
    description: string;
  }>;
}) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-2 text-2xl font-medium">
          Notifications & Email Preferences
        </h2>
        <p className="text-muted-foreground">
          Choose how you'd like to stay connected
        </p>
      </div>

      <div className="space-y-8">
        <Card>
          <CardContent className="p-6">
            <h3 className="mb-4 font-medium">Email Preferences</h3>
            <div className="space-y-4">
              {emailPreferenceOptions.map((pref) => (
                <div
                  key={pref.key}
                  className="flex items-center justify-between"
                >
                  <div>
                    <Label className="font-medium">{pref.label}</Label>
                    <p className="text-sm text-muted-foreground">
                      {pref.description}
                    </p>
                  </div>
                  <Switch
                    checked={
                      formData.emailPreferences?.[
                        pref.key as keyof typeof formData.emailPreferences
                      ] || false
                    }
                    onCheckedChange={(checked) =>
                      updateFormData({
                        emailPreferences: {
                          ...(formData.emailPreferences || {
                            weeklyDigest: true,
                            recipeSuggestions: true,
                            shoppingReminders: false,
                            seasonalUpdates: true,
                          }),
                          [pref.key]: checked,
                        },
                      })
                    }
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h3 className="mb-4 font-medium">Notification Settings</h3>
            <div className="space-y-4">
              {notificationOptions.map((setting) => (
                <div
                  key={setting.key}
                  className="flex items-center justify-between"
                >
                  <div>
                    <Label className="font-medium">{setting.label}</Label>
                    <p className="text-sm text-muted-foreground">
                      {setting.description}
                    </p>
                  </div>
                  <Switch
                    checked={
                      formData.notificationSettings?.[
                        setting.key as keyof typeof formData.notificationSettings
                      ] || false
                    }
                    onCheckedChange={(checked) =>
                      updateFormData({
                        notificationSettings: {
                          ...(formData.notificationSettings || {
                            pushNotifications: true,
                            emailNotifications: true,
                            smsNotifications: false,
                          }),
                          [setting.key]: checked,
                        },
                      })
                    }
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function RecipeDNAStep({
  formData,
  updateFormData,
  texturePreferences,
  cookingMethods,
}: {
  formData: Partial<TasteProfileData>;
  updateFormData: (data: Partial<TasteProfileData>) => void;
  texturePreferences: string[];
  cookingMethods: string[];
}) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-2 text-2xl font-medium">Recipe DNA</h2>
        <p className="text-muted-foreground">
          Fine-tune your taste profile for perfect recipe matches
        </p>
      </div>

      <div className="space-y-8">
        <div>
          <Label className="mb-4 block text-base font-medium">
            Spice Tolerance: {formData.tasteProfile?.spiceTolerance || 5}/10
          </Label>
          <Slider
            value={[formData.tasteProfile?.spiceTolerance || 5]}
            onValueChange={([value]) =>
              updateFormData({
                tasteProfile: {
                  ...(formData.tasteProfile || {
                    spiceTolerance: 5,
                    sweetnessPreference: 5,
                    complexityLevel: 5,
                    texturePreferences: [],
                    cookingMethods: [],
                  }),
                  spiceTolerance: value,
                },
              })
            }
            max={10}
            min={1}
            step={1}
            className="w-full"
          />
          <div className="mt-1 flex justify-between text-xs text-muted-foreground">
            <span>Mild</span>
            <span>Medium</span>
            <span>Very Hot</span>
          </div>
        </div>

        <div>
          <Label className="mb-4 block text-base font-medium">
            Sweetness Preference:{" "}
            {formData.tasteProfile?.sweetnessPreference || 5}/10
          </Label>
          <Slider
            value={[formData.tasteProfile?.sweetnessPreference || 5]}
            onValueChange={([value]) =>
              updateFormData({
                tasteProfile: {
                  ...(formData.tasteProfile || {
                    spiceTolerance: 5,
                    sweetnessPreference: 5,
                    complexityLevel: 5,
                    texturePreferences: [],
                    cookingMethods: [],
                  }),
                  sweetnessPreference: value,
                },
              })
            }
            max={10}
            min={1}
            step={1}
            className="w-full"
          />
          <div className="mt-1 flex justify-between text-xs text-muted-foreground">
            <span>Not Sweet</span>
            <span>Balanced</span>
            <span>Very Sweet</span>
          </div>
        </div>

        <div>
          <Label className="mb-4 block text-base font-medium">
            Recipe Complexity: {formData.tasteProfile?.complexityLevel || 5}/10
          </Label>
          <Slider
            value={[formData.tasteProfile?.complexityLevel || 5]}
            onValueChange={([value]) =>
              updateFormData({
                tasteProfile: {
                  ...(formData.tasteProfile || {
                    spiceTolerance: 5,
                    sweetnessPreference: 5,
                    complexityLevel: 5,
                    texturePreferences: [],
                    cookingMethods: [],
                  }),
                  complexityLevel: value,
                },
              })
            }
            max={10}
            min={1}
            step={1}
            className="w-full"
          />
          <div className="mt-1 flex justify-between text-xs text-muted-foreground">
            <span>Simple</span>
            <span>Moderate</span>
            <span>Complex</span>
          </div>
        </div>

        <div>
          <Label className="mb-4 block text-base font-medium">
            Preferred textures (Select all that apply)
          </Label>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {texturePreferences.map((texture) => (
              <div key={texture} className="flex items-center space-x-2">
                <Checkbox
                  id={`texture-${texture}`}
                  checked={
                    formData.tasteProfile?.texturePreferences?.includes(
                      texture,
                    ) || false
                  }
                  onCheckedChange={(checked) => {
                    const current =
                      formData.tasteProfile?.texturePreferences || [];
                    const updated = checked
                      ? [...current, texture]
                      : current.filter((t) => t !== texture);
                    updateFormData({
                      tasteProfile: {
                        ...(formData.tasteProfile || {
                          spiceTolerance: 5,
                          sweetnessPreference: 5,
                          complexityLevel: 5,
                          texturePreferences: [],
                          cookingMethods: [],
                        }),
                        texturePreferences: updated,
                      },
                    });
                  }}
                />
                <Label
                  htmlFor={`texture-${texture}`}
                  className="text-sm capitalize"
                >
                  {texture}
                </Label>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Label className="mb-4 block text-base font-medium">
            Preferred cooking methods (Select all that apply)
          </Label>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {cookingMethods.map((method) => (
              <div key={method} className="flex items-center space-x-2">
                <Checkbox
                  id={`method-${method}`}
                  checked={
                    formData.tasteProfile?.cookingMethods?.includes(method) ||
                    false
                  }
                  onCheckedChange={(checked) => {
                    const current = formData.tasteProfile?.cookingMethods || [];
                    const updated = checked
                      ? [...current, method]
                      : current.filter((m) => m !== method);
                    updateFormData({
                      tasteProfile: {
                        ...(formData.tasteProfile || {
                          spiceTolerance: 5,
                          sweetnessPreference: 5,
                          complexityLevel: 5,
                          texturePreferences: [],
                          cookingMethods: [],
                        }),
                        cookingMethods: updated,
                      },
                    });
                  }}
                />
                <Label
                  htmlFor={`method-${method}`}
                  className="text-sm capitalize"
                >
                  {method}
                </Label>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// NEW: Meal Planning Preferences Step
function MealPlanningStep({
  formData,
  updateFormData,
  mealTypeOptions,
  budgetOptions,
  difficultyOptions,
}: {
  formData: Partial<TasteProfileData>;
  updateFormData: (data: Partial<TasteProfileData>) => void;
  mealTypeOptions: Array<{
    id: string;
    icon: string;
    title: string;
    description: string;
  }>;
  budgetOptions: Array<{
    id: string;
    icon: string;
    title: string;
    description: string;
  }>;
  difficultyOptions: Array<{
    id: string;
    icon: string;
    title: string;
    description: string;
  }>;
}) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-2 text-2xl font-medium">Meal Planning Preferences</h2>
        <p className="text-muted-foreground">
          Let's customize your meal planning experience for perfect results
        </p>
      </div>

      <div className="space-y-8">
        {/* Meal Types */}
        <div>
          <Label className="mb-4 block text-base font-medium">
            Which meals would you like to plan? (Select all that apply)
          </Label>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {mealTypeOptions.map((mealType) => (
              <div
                key={mealType.id}
                className={`cursor-pointer rounded-lg border p-4 text-center transition-all ${
                  formData.mealTypes?.includes(mealType.id)
                    ? "border-coral bg-coral text-white"
                    : "hover:bg-stone/20 border-border bg-white"
                }`}
                onClick={() => {
                  const current = formData.mealTypes || [];
                  const updated = current.includes(mealType.id)
                    ? current.filter((m) => m !== mealType.id)
                    : [...current, mealType.id];
                  updateFormData({ mealTypes: updated });
                }}
              >
                <div className="mb-2">
                  {mealType.icon === "Coffee" && (
                    <Coffee className="mx-auto h-6 w-6" />
                  )}
                  {mealType.icon === "Sun" && (
                    <Sun className="mx-auto h-6 w-6" />
                  )}
                  {mealType.icon === "Moon" && (
                    <Moon className="mx-auto h-6 w-6" />
                  )}
                  {mealType.icon === "Apple" && (
                    <Apple className="mx-auto h-6 w-6" />
                  )}
                </div>
                <div className="font-medium">{mealType.title}</div>
                <div className="text-xs opacity-80">{mealType.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Budget Range */}
        <div>
          <Label className="mb-4 block text-base font-medium">
            What's your preferred budget range per serving?
          </Label>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {budgetOptions.map((budget) => (
              <div
                key={budget.id}
                className={`cursor-pointer rounded-lg border p-4 text-center transition-all ${
                  formData.budgetRange === budget.id
                    ? "border-coral bg-coral text-white"
                    : "hover:bg-stone/20 border-border bg-white"
                }`}
                onClick={() => updateFormData({ budgetRange: budget.id })}
              >
                <div className="mb-2">
                  {budget.icon === "DollarSign" && (
                    <DollarSign className="mx-auto h-6 w-6" />
                  )}
                  {budget.icon === "TrendingUp" && (
                    <TrendingUp className="mx-auto h-6 w-6" />
                  )}
                  {budget.icon === "Star" && (
                    <Star className="mx-auto h-6 w-6" />
                  )}
                </div>
                <div className="font-medium">{budget.title}</div>
                <div className="text-sm opacity-80">{budget.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Cooking Difficulty */}
        <div>
          <Label className="mb-4 block text-base font-medium">
            What's your cooking skill level?
          </Label>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {difficultyOptions.map((difficulty) => (
              <div
                key={difficulty.id}
                className={`cursor-pointer rounded-lg border p-4 text-center transition-all ${
                  formData.cookingDifficulty === difficulty.id
                    ? "border-coral bg-coral text-white"
                    : "hover:bg-stone/20 border-border bg-white"
                }`}
                onClick={() =>
                  updateFormData({ cookingDifficulty: difficulty.id })
                }
              >
                <div className="mb-2">
                  {difficulty.icon === "Smile" && (
                    <Smile className="mx-auto h-6 w-6" />
                  )}
                  {difficulty.icon === "ChefHat" && (
                    <ChefHat className="mx-auto h-6 w-6" />
                  )}
                  {difficulty.icon === "Award" && (
                    <Award className="mx-auto h-6 w-6" />
                  )}
                </div>
                <div className="font-medium">{difficulty.title}</div>
                <div className="text-sm opacity-80">
                  {difficulty.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewStep({
  formData,
  updateFormData,
}: {
  formData: Partial<TasteProfileData>;
  updateFormData: (data: Partial<TasteProfileData>) => void;
}) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-2 text-2xl font-medium">Review Your Taste Profile</h2>
        <p className="text-muted-foreground">
          Review and confirm your preferences before we create your personalized
          experience
        </p>
      </div>

      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <h3 className="mb-4 font-medium">Personal & Location</h3>
            <div className="space-y-2 text-sm">
              <p>
                <strong>Location:</strong>{" "}
                {formData.regionCountry
                  ? `${formData.regionCountry} (${formData.regionCurrency})`
                  : "Not specified"}
              </p>
              <p>
                <strong>Household Size:</strong> {formData.householdSize}{" "}
                {formData.householdSize === 1 ? "person" : "people"}
              </p>
              {(formData.reasons?.length || 0) > 0 && (
                <p>
                  <strong>Goals:</strong> {formData.reasons?.join(", ")}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h3 className="mb-4 font-medium">Dietary & Restrictions</h3>
            <div className="space-y-2 text-sm">
              <p>
                <strong>Diet Type:</strong>{" "}
                {formData.dietType
                  ? formData.dietType.charAt(0).toUpperCase() +
                    formData.dietType.slice(1)
                  : "Not specified"}
              </p>
              {(formData.allergies?.length || 0) > 0 && (
                <p>
                  <strong>Allergies:</strong> {formData.allergies?.join(", ")}
                </p>
              )}
              {(formData.dietaryRestrictions?.length || 0) > 0 && (
                <p>
                  <strong>Restrictions:</strong>{" "}
                  {formData.dietaryRestrictions?.join(", ")}
                </p>
              )}
              {(formData.likes?.length || 0) > 0 && (
                <p>
                  <strong>Loves:</strong> {formData.likes?.join(", ")}
                </p>
              )}
              {(formData.dislikes?.length || 0) > 0 && (
                <p>
                  <strong>Avoids:</strong> {formData.dislikes?.join(", ")}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h3 className="mb-4 font-medium">Goals & Kitchen Setup</h3>
            <div className="space-y-2 text-sm">
              <p>
                <strong>Primary Goal:</strong>{" "}
                {formData.primaryGoal
                  ? formData.primaryGoal
                      .replace("-", " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())
                  : "Not specified"}
              </p>
              <p>
                <strong>Equipment:</strong>{" "}
                {(formData.availableEquipment?.length || 0) > 0
                  ? formData.availableEquipment?.join(", ") || "None specified"
                  : "None specified"}
              </p>
              <p>
                <strong>Batch Prep:</strong>{" "}
                {formData.batchPrepPreference
                  ? formData.batchPrepPreference
                      .replace("-", " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())
                  : "Not specified"}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h3 className="mb-4 font-medium">Preferences</h3>
            <div className="space-y-2 text-sm">
              <p>
                <strong>Spice Level:</strong>{" "}
                {formData.spiceLevel
                  ? formData.spiceLevel.charAt(0).toUpperCase() +
                    formData.spiceLevel.slice(1)
                  : "Not specified"}
              </p>
              <p>
                <strong>Cooking Time:</strong>{" "}
                {formData.cookingTime
                  ? formData.cookingTime.replace("min", " minutes")
                  : "Not specified"}
              </p>
              {(formData.favoriteCuisines?.length || 0) > 0 && (
                <p>
                  <strong>Favorite Cuisines:</strong>{" "}
                  {formData.favoriteCuisines?.join(", ") || ""}
                </p>
              )}
              {(formData.pantryStaples?.length || 0) > 0 && (
                <p>
                  <strong>Pantry Staples:</strong>{" "}
                  {formData.pantryStaples?.join(", ") || ""}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h3 className="mb-4 font-medium">Meal Planning</h3>
            <div className="space-y-2 text-sm">
              {(formData.mealTypes?.length || 0) > 0 && (
                <p>
                  <strong>Preferred Meals:</strong>{" "}
                  {formData.mealTypes
                    ?.map(
                      (type) => type.charAt(0).toUpperCase() + type.slice(1),
                    )
                    .join(", ") || ""}
                </p>
              )}
              <p>
                <strong>Budget Range:</strong>{" "}
                {formData.budgetRange
                  ? formData.budgetRange.charAt(0).toUpperCase() +
                    formData.budgetRange.slice(1)
                  : "Not specified"}
              </p>
              <p>
                <strong>Cooking Difficulty:</strong>{" "}
                {formData.cookingDifficulty
                  ? formData.cookingDifficulty.charAt(0).toUpperCase() +
                    formData.cookingDifficulty.slice(1)
                  : "Not specified"}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h3 className="mb-4 font-medium">Recipe DNA</h3>
            <div className="space-y-2 text-sm">
              <p>
                <strong>Spice Tolerance:</strong>{" "}
                {formData.tasteProfile?.spiceTolerance}/10
              </p>
              <p>
                <strong>Sweetness Preference:</strong>{" "}
                {formData.tasteProfile?.sweetnessPreference}/10
              </p>
              <p>
                <strong>Complexity Level:</strong>{" "}
                {formData.tasteProfile?.complexityLevel}/10
              </p>
              {(formData.tasteProfile?.texturePreferences?.length || 0) > 0 && (
                <p>
                  <strong>Textures:</strong>{" "}
                  {formData.tasteProfile?.texturePreferences?.join(", ") || ""}
                </p>
              )}
              {(formData.tasteProfile?.cookingMethods?.length || 0) > 0 && (
                <p>
                  <strong>Cooking Methods:</strong>{" "}
                  {formData.tasteProfile?.cookingMethods?.join(", ") || ""}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="rounded-lg border border-coral/20 bg-coral/5 p-6">
          <h3 className="mb-2 font-medium text-coral">
            Ready to create your personalized meal planning experience?
          </h3>
          <p className="text-sm text-muted-foreground">
            We'll use all this information to create meal plans perfectly
            tailored to your taste, dietary needs, and lifestyle. You can always
            update these preferences later in your profile settings.
          </p>
        </div>
      </div>
    </div>
  );
}

// New Step Components for 5-Step Flow

function EssentialsStep({
  formData,
  updateFormData,
  countries,
  reasonOptions,
  householdOptions,
}: {
  formData: Partial<TasteProfileData>;
  updateFormData: (data: Partial<TasteProfileData>) => void;
  countries: Array<{
    code: string;
    name: string;
    currency: string;
    flag: string;
  }>;
  reasonOptions: string[];
  householdOptions: Array<{
    id: number;
    label: string;
  }>;
}) {
  const toggleReason = (reason: string) => {
    const current = formData.reasons || [];
    const updated = current.includes(reason)
      ? current.filter((r) => r !== reason)
      : [...current, reason];
    updateFormData({ reasons: updated });
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-2 text-2xl font-medium">Welcome to Nura! 🌟</h2>
        <p className="text-muted-foreground">
          Let's personalize your meal planning experience. We'll use your
          preferences to suggest recipes you'll love.
        </p>
      </div>

      <div className="space-y-6">
        {/* Household Size */}
        <div>
          <Label className="mb-4 block text-base font-medium">
            How many people are you cooking for?
          </Label>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {householdOptions.map((option) => (
              <Button
                key={option.id}
                type="button"
                variant={
                  formData.householdSize === option.id ? "default" : "outline"
                }
                className={
                  formData.householdSize === option.id
                    ? "border-coral bg-coral text-white"
                    : "hover:bg-stone/20 border-border"
                }
                onClick={() => updateFormData({ householdSize: option.id })}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Region/Country Selection */}
        <div>
          <Label className="mb-4 block text-base font-medium">
            Where are you shopping from?
            <span className="mt-1 block text-sm font-normal text-muted-foreground">
              This helps us estimate grocery costs and show your local currency.
            </span>
          </Label>
          <Select
            value={formData.regionCountry || "US"}
            onValueChange={(value) => {
              const selectedCountry = countries.find((c) => c.code === value);
              updateFormData({
                regionCountry: value,
                regionCurrency: selectedCountry?.currency || "USD",
              });
            }}
          >
            <SelectTrigger>
              <SelectValue>
                {formData.regionCountry &&
                countries.find((c) => c.code === formData.regionCountry) ? (
                  <span className="flex items-center gap-2">
                    <span>
                      {
                        countries.find((c) => c.code === formData.regionCountry)
                          ?.flag
                      }
                    </span>
                    <span>
                      {
                        countries.find((c) => c.code === formData.regionCountry)
                          ?.name
                      }
                    </span>
                    <span className="text-muted-foreground">
                      (
                      {
                        countries.find((c) => c.code === formData.regionCountry)
                          ?.currency
                      }
                      )
                    </span>
                  </span>
                ) : (
                  "Select your country"
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {countries.map((country) => (
                <SelectItem key={country.code} value={country.code}>
                  <span className="flex items-center gap-2">
                    <span>{country.flag}</span>
                    <span>{country.name}</span>
                    <span className="text-muted-foreground">
                      ({country.currency})
                    </span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Why are you here? */}
        <div>
          <Label className="mb-4 block text-base font-medium">
            What brings you to meal planning today?
            <span className="mt-1 block text-sm font-normal text-muted-foreground">
              Pick one or more — you can change this anytime.
            </span>
          </Label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {reasonOptions.map((reason) => (
              <button
                key={reason}
                type="button"
                onClick={() => toggleReason(reason)}
                className={`rounded-lg border p-3 text-left text-sm transition-all ${
                  formData.reasons?.includes(reason)
                    ? "border-coral bg-coral/10 text-ink"
                    : "hover:bg-stone/20 border-border bg-white text-ink"
                }`}
              >
                {reason}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function DietaryAndGoalsStep({
  formData,
  updateFormData,
  dietTypes,
  commonAllergies,
  commonRestrictions,
  goals,
}: {
  formData: Partial<TasteProfileData>;
  updateFormData: (data: Partial<TasteProfileData>) => void;
  dietTypes: Array<{
    id: string;
    emoji: string;
    title: string;
    description: string;
  }>;
  commonAllergies: string[];
  commonRestrictions: string[];
  goals: Array<{
    id: string;
    emoji: string;
    title: string;
    description: string;
  }>;
}) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-2 text-2xl font-medium">Your Diet & Goals</h2>
        <p className="text-muted-foreground">
          Tell us about your diet and what you want to achieve – we'll tailor
          recipes to fit your lifestyle.
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <Label className="mb-4 block text-base font-medium">
            What's your primary diet type?
          </Label>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {dietTypes.map((diet) => (
              <ChoiceCard
                key={diet.id}
                emoji={diet.emoji}
                title={diet.title}
                description={diet.description}
                selected={formData.dietType === diet.id}
                onClick={() => updateFormData({ dietType: diet.id })}
              />
            ))}
          </div>
        </div>

        <div>
          <Label className="mb-4 block text-base font-medium">
            Any allergies? (Select all that apply)
          </Label>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {commonAllergies.map((allergy) => (
              <div key={allergy} className="flex items-center space-x-2">
                <Checkbox
                  id={`allergy-${allergy}`}
                  checked={formData.allergies?.includes(allergy) || false}
                  onCheckedChange={(checked) => {
                    const current = formData.allergies || [];
                    const updated = checked
                      ? [...current, allergy]
                      : current.filter((a) => a !== allergy);
                    updateFormData({ allergies: updated });
                  }}
                />
                <Label
                  htmlFor={`allergy-${allergy}`}
                  className="text-sm capitalize"
                >
                  {allergy}
                </Label>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Label className="mb-4 block text-base font-medium">
            Any dietary restrictions? (Select all that apply)
          </Label>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {commonRestrictions.map((restriction) => (
              <div key={restriction} className="flex items-center space-x-2">
                <Checkbox
                  id={`restriction-${restriction}`}
                  checked={
                    formData.dietaryRestrictions?.includes(restriction) || false
                  }
                  onCheckedChange={(checked) => {
                    const current = formData.dietaryRestrictions || [];
                    const updated = checked
                      ? [...current, restriction]
                      : current.filter((r) => r !== restriction);
                    updateFormData({ dietaryRestrictions: updated });
                  }}
                />
                <Label
                  htmlFor={`restriction-${restriction}`}
                  className="text-sm capitalize"
                >
                  {restriction}
                </Label>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Label className="mb-4 block text-base font-medium">
            What's your primary goal?
          </Label>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {goals.map((goal) => (
              <ChoiceCard
                key={goal.id}
                emoji={goal.emoji}
                title={goal.title}
                description={goal.description}
                selected={formData.primaryGoal === goal.id}
                onClick={() => updateFormData({ primaryGoal: goal.id })}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function RecipeSelectionStep({
  formData,
  updateFormData,
}: {
  formData: Partial<TasteProfileData>;
  updateFormData: (data: Partial<TasteProfileData>) => void;
}) {
  const [selections, setSelections] = useState<
    { chosenId: string; rejectedId: string }[]
  >([]);
  const [pairs, setPairs] = useState<any[]>([]); // Will be fetched from backend

  useEffect(() => {
    // Fetch recipe pairs from backend
    // For now, use placeholder pairs
    setPairs([
      [
        {
          id: "1",
          title: "Italian Pasta",
          cuisine: "Italian",
          totalMinutes: 30,
          difficulty: "Easy",
        },
        {
          id: "2",
          title: "Asian Stir Fry",
          cuisine: "Asian",
          totalMinutes: 20,
          difficulty: "Easy",
        },
      ],
      [
        {
          id: "3",
          title: "Quick Salad",
          cuisine: "American",
          totalMinutes: 15,
          difficulty: "Easy",
        },
        {
          id: "4",
          title: "Slow Stew",
          cuisine: "Mediterranean",
          totalMinutes: 60,
          difficulty: "Medium",
        },
      ],
      [
        {
          id: "5",
          title: "Beef Burger",
          cuisine: "American",
          totalMinutes: 25,
          difficulty: "Easy",
        },
        {
          id: "6",
          title: "Veggie Curry",
          cuisine: "Indian",
          totalMinutes: 40,
          difficulty: "Medium",
        },
      ],
    ]);
  }, []);

  const handleSelectionsChange = (
    newSelections: { chosenId: string; rejectedId: string }[],
  ) => {
    setSelections(newSelections);
  };

  const handleComplete = () => {
    // Submit selections to backend
    updateFormData({});
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-2 text-2xl font-medium">Pick Your Favorites</h2>
        <p className="text-muted-foreground">
          Choose your favorite from each pair – this helps us learn your taste
          and recommend better recipes.
        </p>
      </div>
      <ColdStartRecipeSelector
        pairs={pairs}
        onSelectionsChange={handleSelectionsChange}
        onComplete={handleComplete}
      />
    </div>
  );
}

function TastePreferencesStep({
  formData,
  updateFormData,
  spiceLevels,
  cookingTimes,
  cuisines,
  pantryStaples,
  texturePreferences,
  cookingMethods,
}: {
  formData: Partial<TasteProfileData>;
  updateFormData: (data: Partial<TasteProfileData>) => void;
  spiceLevels: Array<{
    id: string;
    emoji: string;
    title: string;
    description: string;
  }>;
  cookingTimes: Array<{
    id: string;
    title: string;
    description: string;
  }>;
  cuisines: string[];
  pantryStaples: string[];
  texturePreferences: string[];
  cookingMethods: string[];
}) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-2 text-2xl font-medium">Taste Preferences</h2>
        <p className="text-muted-foreground">
          Let's fine-tune your preferences for spot-on recipe recommendations.
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <Label className="mb-4 block text-base font-medium">
            What's your spice tolerance?
          </Label>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {spiceLevels.map((level) => (
              <ChoiceCard
                key={level.id}
                emoji={level.emoji}
                title={level.title}
                description={level.description}
                selected={formData.spiceLevel === level.id}
                onClick={() => updateFormData({ spiceLevel: level.id })}
              />
            ))}
          </div>
        </div>

        <div>
          <Label className="mb-4 block text-base font-medium">
            How much time do you want to spend cooking?
          </Label>
          <Select
            value={formData.cookingTime || ""}
            onValueChange={(value: string) =>
              updateFormData({ cookingTime: value || "" })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select cooking time preference" />
            </SelectTrigger>
            <SelectContent>
              {cookingTimes.map((time) => (
                <SelectItem key={time.id} value={time.id}>
                  {time.title} - {time.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="mb-4 block text-base font-medium">
            Favorite cuisines (Select all that apply)
          </Label>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {cuisines.map((cuisine) => (
              <div key={cuisine} className="flex items-center space-x-2">
                <Checkbox
                  id={`cuisine-${cuisine}`}
                  checked={
                    formData.favoriteCuisines?.includes(cuisine) || false
                  }
                  onCheckedChange={(checked) => {
                    const current = formData.favoriteCuisines || [];
                    const updated = checked
                      ? [...current, cuisine]
                      : current.filter((c) => c !== cuisine);
                    updateFormData({ favoriteCuisines: updated });
                  }}
                />
                <Label htmlFor={`cuisine-${cuisine}`} className="text-sm">
                  {cuisine}
                </Label>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Label className="mb-4 block text-base font-medium">
            Preferred textures (Select all that apply)
          </Label>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {texturePreferences.map((texture) => (
              <div key={texture} className="flex items-center space-x-2">
                <Checkbox
                  id={`texture-${texture}`}
                  checked={
                    formData.tasteProfile?.texturePreferences?.includes(
                      texture,
                    ) || false
                  }
                  onCheckedChange={(checked) => {
                    const current =
                      formData.tasteProfile?.texturePreferences || [];
                    const updated = checked
                      ? [...current, texture]
                      : current.filter((t) => t !== texture);
                    updateFormData({
                      tasteProfile: {
                        ...(formData.tasteProfile || {
                          spiceTolerance: 5,
                          sweetnessPreference: 5,
                          complexityLevel: 5,
                          texturePreferences: [],
                          cookingMethods: [],
                        }),
                        texturePreferences: updated,
                      },
                    });
                  }}
                />
                <Label
                  htmlFor={`texture-${texture}`}
                  className="text-sm capitalize"
                >
                  {texture}
                </Label>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function InitialRecipeRecommendationsStep({
  formData,
  onComplete,
}: {
  formData: Partial<TasteProfileData>;
  onComplete: () => void;
}) {
  // Get action references - these are auto-generated by Wasp from main.wasp
  const operations = require("wasp/client/operations");
  const getRecipes = useAction(operations.getInitialRecipeRecommendations);
  const recordSelections = useAction(operations.recordInitialRecipeSelections);

  const [loading, setLoading] = useState(true);
  const [recipes, setRecipes] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    const loadRecipes = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getRecipes({ formData });
        setRecipes(data as any[]);
      } catch (e: any) {
        setError(e?.message || "Failed to load recipes");
      } finally {
        setLoading(false);
      }
    };
    loadRecipes();
  }, [getRecipes]);

  const handleSelectRecipe = (recipeId: string) => {
    if (selectedIds.includes(recipeId)) {
      setSelectedIds(selectedIds.filter((id) => id !== recipeId));
    } else if (selectedIds.length < 3) {
      setSelectedIds([...selectedIds, recipeId]);
    }
  };

  const handleComplete = async () => {
    try {
      setIsSubmitting(true);
      await recordSelections({ selectedRecipeIds: selectedIds });
      onComplete();
    } catch (e: any) {
      setError(e?.message || "Failed to save selections");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div>
          <h2 className="mb-2 text-2xl font-medium">🎉 Your Perfect Recipes</h2>
          <p className="text-muted-foreground">
            Loading personalized recipe recommendations based on your
            preferences...
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="mb-2 aspect-video rounded bg-muted" />
              <div className="mb-2 h-4 w-24 rounded bg-muted" />
              <div className="h-3 w-full rounded bg-muted" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div>
          <h2 className="mb-2 text-2xl font-medium">⚠️ Oops!</h2>
          <p className="text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-2 text-2xl font-medium">🎉 Your Perfect Recipes</h2>
        <p className="text-muted-foreground">
          We've picked 6 recipes tailored to your preferences. Pick your top 3
          favorites to help us improve recommendations!
        </p>
      </div>

      <div>
        <div className="mb-4 flex items-center justify-between">
          <Label className="text-base font-medium">
            Select your top 3 recipes
          </Label>
          <span
            className={`text-sm font-medium ${
              selectedIds.length === 3 ? "text-coral" : "text-muted-foreground"
            }`}
          >
            {selectedIds.length}/3 selected
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          {recipes.length > 0 ? (
            recipes.map((recipe) => (
              <button
                key={recipe.id}
                onClick={() => handleSelectRecipe(recipe.id)}
                className={`group overflow-hidden rounded-lg border-2 transition-all ${
                  selectedIds.includes(recipe.id)
                    ? "border-coral ring-2 ring-coral/50"
                    : "border-border hover:border-coral/50"
                }`}
              >
                <div className="aspect-video overflow-hidden bg-muted">
                  {recipe.heroImageUrl ? (
                    <img
                      src={recipe.heroImageUrl}
                      alt={recipe.title}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="grid h-full w-full place-items-center text-xs text-muted-foreground">
                      No image
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <div className="text-left font-medium">{recipe.title}</div>
                  <div className="mt-2 flex flex-wrap gap-1 text-xs text-muted-foreground">
                    {recipe.cuisine && (
                      <span className="rounded bg-muted px-2 py-0.5">
                        {recipe.cuisine}
                      </span>
                    )}
                    {recipe.totalMinutes && (
                      <span className="rounded bg-muted px-2 py-0.5">
                        {recipe.totalMinutes} min
                      </span>
                    )}
                    {recipe.difficulty && (
                      <span className="rounded bg-muted px-2 py-0.5">
                        {recipe.difficulty}
                      </span>
                    )}
                  </div>
                </div>
                {selectedIds.includes(recipe.id) && (
                  <div className="absolute inset-0 flex items-center justify-center bg-coral/10">
                    <div className="rounded-full bg-coral p-2 text-white">
                      ✓
                    </div>
                  </div>
                )}
              </button>
            ))
          ) : (
            <div className="col-span-full rounded-lg border-2 border-dashed border-muted-foreground/25 p-8 text-center">
              <p className="text-muted-foreground">
                No recipes available. Please try again.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={onComplete}
          disabled={isSubmitting}
          className="flex-1"
        >
          Skip this step
        </Button>
        <Button
          onClick={handleComplete}
          disabled={selectedIds.length !== 3 || isSubmitting}
          className="flex-1 bg-coral text-white hover:bg-coral/90"
        >
          {isSubmitting ? "Saving..." : "Continue with selections"}
        </Button>
      </div>
    </div>
  );
}
